import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Compass, Building2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * ShortlistLandRecommender - given the user's saved plans (full_slugs),
 * recommends live Adelaide land blocks where ANY of the saved plans
 * would physically fit (frontage + depth check on the backend).
 *
 * Auto-hides when no shortlisted plans OR no matching land.
 *
 * Drop on: FavouritesSidebar (just below the plan card list).
 * Endpoint: POST /api/cross-link/match-shortlist
 *   payload: { "plan_slugs": ["builder-slug/plan-slug", ...] }
 */
const ShortlistLandRecommender = ({ planSlugs, onLinkClick }) => {
  const [land, setLand] = useState([]);

  useEffect(() => {
    if (!Array.isArray(planSlugs) || planSlugs.length === 0) return;
    let alive = true;
    fetch(`${API}/api/cross-link/match-shortlist`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ plan_slugs: planSlugs }),
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(j => { if (alive) setLand(j.matching_land || []); })
      .catch(() => { if (alive) setLand([]); });
    return () => { alive = false; };
  }, [planSlugs]);

  if (!Array.isArray(planSlugs) || planSlugs.length === 0) return null;
  if (land.length === 0) return null;

  return (
    <div
      data-testid="shortlist-land-recommender"
      className="mt-4 p-3 bg-gradient-to-br from-[#eef6ff] to-white border border-[#bfdcff] rounded-xl"
    >
      <div className="flex items-center gap-1.5 mb-2">
        <Compass className="w-3.5 h-3.5 text-[#1d8efe]" />
        <p className="text-[11px] font-semibold uppercase tracking-wider text-[#1d8efe]">
          Land that may fit your shortlist
        </p>
      </div>
      <p className="text-[11.5px] text-[#5a5a5f] mb-3 leading-snug">
        {land.length} live Adelaide block{land.length === 1 ? '' : 's'} with frontage wide enough for at least one of your saved designs.
      </p>
      <div className="space-y-1.5">
        {land.slice(0, 3).map(li => (
          <Link
            key={li.id}
            to={`/land-for-sale-adelaide/${li.suburb_slug}/${li.listing_slug}`}
            onClick={onLinkClick}
            className="flex items-center gap-2.5 p-2 bg-white rounded-lg hover:border-[#1d8efe] border border-transparent transition-colors"
            data-testid={`shortlist-land-${li.id}`}
          >
            <div className="w-10 h-10 shrink-0 rounded-md overflow-hidden bg-[#e8e8ed]">
              {li.image
                ? <img src={li.image} alt={li.suburb} className="w-full h-full object-cover" loading="lazy" />
                : <div className="w-full h-full flex items-center justify-center"><Building2 className="w-4 h-4 text-[#d2d2d7]" /></div>}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[12px] font-medium text-[#1d1d1f] truncate">
                {li.area_m2 ? `${Math.round(li.area_m2)}m²` : 'Block'} · {li.estate_name || li.suburb}
              </p>
              <p className="text-[10.5px] text-[#86868b] inline-flex items-center gap-0.5">
                <MapPin className="w-2.5 h-2.5" /> {li.suburb}
                {li.frontage_m ? ` · ${li.frontage_m}m frontage` : ''}
              </p>
            </div>
          </Link>
        ))}
      </div>
      {land.length > 3 && (
        <Link
          to="/land-for-sale-adelaide"
          onClick={onLinkClick}
          className="block text-center text-[11px] font-semibold text-[#1d8efe] hover:text-[#1d1d1f] mt-2"
        >
          See all {land.length} matching blocks &rarr;
        </Link>
      )}
      <p className="text-[10.5px] text-[#5a4a1f] mt-2 leading-snug" data-testid="shortlist-land-disclaimer">
        <strong>Footprint check only.</strong> Frontage-based fit estimate - slope, easements, services and council setbacks not assessed. <strong>BuildPilot is not the selling agent and is not liable for any land purchase made on this match.</strong> Confirm with the builder and a licensed surveyor before signing.
      </p>
    </div>
  );
};

export default ShortlistLandRecommender;
